// helpers/mediaPrefetch.ts
//
// Goals:
// - Keep existing public API: getCachedBlob(), clearPrefetchCache(), prefetchImageToBlob(url, signal?)
// - Add in-flight de-dupe so concurrent calls for same canonical URL don't double-fetch
// - Avoid "abort storms": if one caller aborts, it should NOT cancel a shared fetch for everyone
//   (we treat caller AbortSignal as "stop waiting / ignore result" rather than abort the shared fetch)
// - Keep fail TTL behavior
// - Keep MAX_ITEMS LRU-ish eviction
//
// IMPORTANT behavioral note:
// - We do NOT pass the caller's AbortSignal to fetch() anymore, because that would allow one component's
//   cleanup to abort the shared request and cause re-fetch loops.
// - Instead, if caller aborts while awaiting, we stop awaiting and return original url.
//   The fetch still completes and populates cache for others.


// A resolver that can fetch a fresh signed URL for a canonical key.
// The handler should return a NEW signed URL (with query) or null.
// ---- signed-url refresh hook (wired by feed components) ----
let refreshSignedUrlHandler: ((canonicalKey: string) => Promise<string | null>) | null = null

export function setRefreshSignedUrlHandler(
  fn: ((canonicalKey: string) => Promise<string | null>) | null
) {
  refreshSignedUrlHandler = fn
}

type CacheEntry = {
  blobUrl: string
  size: number
  usedAt: number
}

type FailEntry = {
  failedAt: number
  tries: number
}

const MAX_ITEMS = 250

// If a URL fails, don't hammer it every render/scroll tick
const FAIL_TTL_MS = 60 * 1000 // 1 minute
const MAX_FAIL_TRIES = 3

// Cache is keyed by a CANONICAL key (origin + pathname, no query/hash)
const cache = new Map<string, CacheEntry>()
const fails = new Map<string, FailEntry>()

// NEW: in-flight de-dupe keyed by canonical key
const inFlight = new Map<string, Promise<string>>()

const PREFETCH_DEBUG = false

function dlog(...args: any[]) {
  if (!PREFETCH_DEBUG) return

  // keep it greppable
  // eslint-disable-next-line no-console
  console.log('[prefetch]', ...args)
}

function now() {
  return Date.now()
}

function safeParseUrl(url: string): URL | null {
  try {
    return new URL(url)
  } catch {
    return null
  }
}

/**
 * Video CDN assets are protected by CloudFront signed cookies.
 *
 * Image-post assets use presigned URLs, so they do not need cookies.
 */
function isCookieAuthenticatedMedia(url: string): boolean {
  const parsed = safeParseUrl(url)

  if (!parsed) {
    return false
  }

  return parsed.hostname === 'video-cdn.allfans.com'
}

function getFetchCredentials(
  url: string
): RequestCredentials {
  return isCookieAuthenticatedMedia(url)
    ? 'include'
    : 'omit'
}

function canonicalize(url: string): string {
  // Goal: same image across different signed URLs => same key
  // canonical = origin + pathname (strip query + hash)
  const u = safeParseUrl(url)

  if (!u) {
    // last-resort: strip query/hash manually
    const q = url.indexOf('?')
    const h = url.indexOf('#')

    const cut =
      q >= 0 && h >= 0
        ? Math.min(q, h)
        : q >= 0
          ? q
          : h

    return cut >= 0
      ? url.slice(0, cut)
      : url
  }

  return u.origin + u.pathname
}

function canRetry(key: string): boolean {
    const f = fails.get(key)

    if (!f) return true

    if (now() - f.failedAt < FAIL_TTL_MS) {
        return false
    }

    // Allow retries after the cooldown, even if
    // the previous failure count reached the limit.
    if (f.tries >= MAX_FAIL_TRIES) {
        fails.delete(key)
    }

    return true
}

function markFail(key: string) {
  const f = fails.get(key)

  if (!f) {
    fails.set(key, {
      failedAt: now(),
      tries: 1,
    })

    return
  }

  f.failedAt = now()
  f.tries += 1
}

function clearFail(key: string) {
  fails.delete(key)
}

function trimCache() {
  if (cache.size <= MAX_ITEMS) return

  // Convert map -> array without iterator syntax (ES5 target safe)
  const entries: Array<[string, CacheEntry]> = []

  cache.forEach((value, key) => {
    entries.push([key, value])
  })

  // delete least recently used
  entries.sort(
    (a, b) =>
      a[1].usedAt - b[1].usedAt
  )

  const deleteCount = Math.max(
    0,
    cache.size - MAX_ITEMS
  )

  for (let i = 0; i < deleteCount; i++) {
    const pair = entries[i]

    if (!pair) continue

    const key = pair[0]
    const entry = pair[1]

    try {
      URL.revokeObjectURL(
        entry.blobUrl
      )
    } catch {}

    cache.delete(key)
  }
}

export function getCachedBlob(
  url: string
): string | null {
  const key = canonicalize(url)
  const entry = cache.get(key)

  if (!entry) {
    return null
  }

  entry.usedAt = now()

  return entry.blobUrl
}

export function clearPrefetchCache() {
  const entries: Array<[string, CacheEntry]> = []

  cache.forEach((value, key) => {
    entries.push([key, value])
  })

  for (
    let i = 0;
    i < entries.length;
    i++
  ) {
    const entry =
      entries[i]?.[1]

    if (!entry) continue

    try {
      URL.revokeObjectURL(
        entry.blobUrl
      )
    } catch {}
  }

  cache.clear()
  fails.clear()
  inFlight.clear()
}

async function awaitOrAbort<T>(
  p: Promise<T>,
  signal?: AbortSignal
): Promise<T> {
  if (!signal) {
    return await p
  }

  if (signal.aborted) {
    return await Promise.reject(
      Object.assign(
        new Error('Aborted'),
        {
          name: 'AbortError',
        }
      )
    )
  }

  return await new Promise<T>(
    (resolve, reject) => {
      const onAbort = () => {
        signal.removeEventListener(
          'abort',
          onAbort
        )

        reject(
          Object.assign(
            new Error('Aborted'),
            {
              name: 'AbortError',
            }
          )
        )
      }

      signal.addEventListener(
        'abort',
        onAbort,
        {
          once: true,
        }
      )

      p.then(
        (v) => {
          signal.removeEventListener(
            'abort',
            onAbort
          )

          resolve(v)
        },
        (e) => {
          signal.removeEventListener(
            'abort',
            onAbort
          )

          reject(e)
        }
      )
    }
  )
}

async function decodeBlobUrl(
  blobUrl: string
): Promise<void> {
  const img = new Image()

  img.decoding = 'async'
  img.src = blobUrl

  try {
    if (
      typeof img.decode === 'function'
    ) {
      await img.decode()
    }
  } catch {
    // Still allow the blob into cache.
    // <img> may be able to render it even if decode() rejected.
  }
}

async function fetchToBlobUrl(
  requestUrl: string,
  key: string
): Promise<string> {
  const cookieAuthenticated =
    isCookieAuthenticatedMedia(
      requestUrl
    )

  const res = await fetch(
    requestUrl,
    {
      mode: 'cors',
      credentials:
        getFetchCredentials(
          requestUrl
        ),
      cache: 'force-cache',
    }
  )

  /*
   * Presigned image URL expired.
   *
   * Only presigned-URL assets use the refresh handler.
   *
   * Cookie-authenticated video CDN assets rely on
   * CloudFront signed cookies and must NOT enter the
   * presigned URL refresh flow.
   */
  if (
    res.status === 403 &&
    !cookieAuthenticated &&
    refreshSignedUrlHandler
  ) {
    dlog(
      '403_RETRY',
      {
        key,
      }
    )

    console.log(
      '[PREFETCH_403]',
      {
        key,
        originalUrl:
          requestUrl,
      }
    )

    try {
      const refreshed =
        await refreshSignedUrlHandler(
          key
        )

      console.log(
        '[PREFETCH_REFRESHED_URL]',
        {
          key,
          refreshed,
        }
      )

      if (refreshed) {
        const res2 =
          await fetch(
            refreshed,
            {
              mode: 'cors',
              credentials:
                getFetchCredentials(
                  refreshed
                ),
              cache: 'no-store',
            }
          )

        console.log(
          '[PREFETCH_REFRESH_RESPONSE]',
          {
            key,
            status:
              res2.status,
            ok:
              res2.ok,
          }
        )

        if (res2.ok) {
          const blob =
            await res2.blob()

          if (blob?.size) {
            const blobUrl =
              URL.createObjectURL(
                blob
              )

            /*
             * IMPORTANT:
             * Decode BEFORE exposing this blob through getCachedBlob().
             */
            await decodeBlobUrl(
              blobUrl
            )

            cache.set(
              key,
              {
                blobUrl,
                size:
                  blob.size,
                usedAt:
                  now(),
              }
            )

            clearFail(key)
            trimCache()

            return blobUrl
          }
        }
      }
    } catch {
      // fall through
    }

    markFail(key)

    return requestUrl
  }

  /*
   * A cookie-authenticated 403 falls through here.
   *
   * Do not try to refresh it as a presigned image URL.
   */
  if (!res.ok) {
    markFail(key)

    return requestUrl
  }

  const blob =
    await res.blob()

  if (!blob?.size) {
    markFail(key)

    return requestUrl
  }

  const blobUrl =
    URL.createObjectURL(blob)

  /*
   * IMPORTANT:
   * Decode BEFORE putting it into the synchronous cache.
   *
   * Once getCachedBlob() returns this URL, we want it to mean:
   *
   * "prefetched AND decode has already been attempted"
   */
  await decodeBlobUrl(
    blobUrl
  )

  cache.set(
    key,
    {
      blobUrl,
      size:
        blob.size,
      usedAt:
        now(),
    }
  )

  clearFail(key)
  trimCache()

  return blobUrl
}

export async function prefetchImageToBlob(
  url: string,
  signal?: AbortSignal
): Promise<string> {
  /*
  console.log('[PREFETCH_START]', {
    url,
    key: canonicalize(url),
    cached: cache.has(canonicalize(url)),
    failed: fails.get(canonicalize(url)),
    inflight: inFlight.has(canonicalize(url)),
  })
  */

  const key =
    canonicalize(url)

  const hit =
    cache.get(key)

  if (hit) {
    hit.usedAt = now()

    dlog(
      'HIT',
      {
        key,
      }
    )

    return hit.blobUrl
  }

  if (!canRetry(key)) {
    dlog(
      'FAIL_TTL',
      {
        key,
      }
    )

    return url
  }

  const existing =
    inFlight.get(key)

  if (existing) {
    dlog(
      'INFLIGHT',
      {
        key,
      }
    )

    try {
      return await awaitOrAbort(
        existing,
        signal
      )
    } catch (e: any) {
      if (
        e?.name ===
        'AbortError'
      ) {
        return url
      }

      return url
    }
  }

  const requestUrl =
    url

  const p =
    (async () => {
      try {
        const out =
          await fetchToBlobUrl(
            requestUrl,
            key
          )

        return out
      } catch {
        markFail(key)

        return requestUrl
      } finally {
        // Ensure inFlight is cleared even if request fails.
        inFlight.delete(
          key
        )
      }
    })()

  inFlight.set(
    key,
    p
  )

  try {
    return await awaitOrAbort(
      p,
      signal
    )
  } catch (e: any) {
    if (
      e?.name ===
      'AbortError'
    ) {
      return url
    }

    return url
  }
}